<script setup lang="ts">
import { ref } from 'vue';
import Button from 'primevue/button';
import ConfirmPopup from 'primevue/confirmpopup';
import { useToast } from 'primevue/usetoast';
import { useConfirm } from 'src/composables/useConfirm';
import { useOfferService } from 'src/services/offer-service';

const props = defineProps<{
  ticketId?: string;
}>();

const { confirm } = useConfirm();
const toast = useToast();
const { sendOffer } = useOfferService();

const selectedOptions = ref<string[]>([]);
const loading = ref(false);

const options = [
  { label: 'offerte', icon: 'pi pi-envelope', action: 'offer' },
  { label: 'opdrachtbevestiging', icon: 'pi pi-check' },
  { label: 'betaalverzoek', icon: 'pi pi-money-bill' },
  { label: 'schademelding', icon: 'pi pi-exclamation-circle' },
  { label: 'schadespecificatie', icon: 'pi pi-info-circle' },
];

async function handleSendEmails() {
  const hasOfferte = selectedOptions.value.includes('offerte');
  const otherOptions = selectedOptions.value.filter(opt => opt !== 'offerte');

  loading.value = true;

  try {
    // Handle offerte separately - call the send offer API
    if (hasOfferte && props.ticketId) {
      const result = await sendOffer(props.ticketId);
      if (!result.success) {
        // Error notification is shown by the service
        return;
      }
    }

    // For other options, show placeholder toast (not implemented yet)
    if (otherOptions.length > 0) {
      toast.add({
        severity: 'info',
        summary: 'Not implemented',
        detail: `Email types not yet implemented: ${otherOptions.join(', ')}`,
        life: 3000,
      });
    }

    // If only offerte was selected and it succeeded, show success
    if (hasOfferte && otherOptions.length === 0) {
      // Success notification already shown by the service
    } else if (!hasOfferte && otherOptions.length > 0) {
      // Only other options selected - already showed the info toast above
    }
  } finally {
    loading.value = false;
    selectedOptions.value = [];
  }
}

function onClickAction(e: Event) {
  const { onAccept, onReject } = confirm({
    ...e,
    group: 'email-button',
  });

  onAccept(() => {
    handleSendEmails();
  });

  onReject(() => {
    selectedOptions.value = [];
  });
}

</script>

<template>
  <div>
    <ConfirmPopup group="email-button">
      <template #message>
        <div class="p-3">
          <Listbox
            v-model="selectedOptions"
            :options="options"
            option-value="label"
            multiple
            class="w-full"
          >
            <template #option="slotProps">
              <div class="flex align-items-center">
                <span
                  :class="slotProps.option.icon"
                  class="mr-2"
                />
                <div>{{ slotProps.option.label }}</div>
              </div>
            </template>
          </Listbox>
        </div>
      </template>
    </ConfirmPopup>
    <Button
      label="Email"
      icon="pi pi-envelope"
      :loading="loading"
      @click="onClickAction"
    />
  </div>
</template>
